import React from 'react';
import { Contact, User as UserType } from '../types';
import { Building2, ChevronRight } from 'lucide-react';

interface ContactListProps {
  contacts: Contact[];
  currentUser: UserType;
  onSelectContact: (contact: Contact) => void;
  onAddContact: () => void;
}

const ContactList: React.FC<ContactListProps> = ({ contacts, currentUser, onSelectContact, onAddContact }) => {
  const userRole = currentUser.role;
  // Allow Admin, Add/Edit, Director, and Sales roles to register new customers
  const canRegister = userRole === 'Admin' || userRole === 'Add/Edit' || userRole === 'Director' || userRole === 'Sales';

  return (
    <div className="p-8">
      <div className="flex justify-between items-end mb-8 px-2">
        <div>
          <h2 className="text-3xl font-black text-slate-800 tracking-tight">Personnel Directory</h2>
          <p className="text-slate-500 font-medium mt-1">Registry of authorized counterparties and commercial partners.</p>
        </div>
        {canRegister && (
          <button 
            onClick={onAddContact}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl shadow-indigo-100 flex items-center gap-3 active:scale-95"
          >
            <Building2 size={18} /> Register Customer
          </button>
        )}
      </div>

      <div className="bg-white rounded-[40px] shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full">
            <thead className="bg-slate-50/80 border-b border-slate-200">
                <tr>
                <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Company Entity</th>
                <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Tax Compliance</th>
                <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Person</th>
                <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Sentiment</th>
                <th className="px-8 py-5 text-right"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {contacts.map((contact) => (
                <tr 
                    key={contact.id} 
                    onClick={() => onSelectContact(contact)}
                    className="hover:bg-slate-50/50 cursor-pointer transition-colors group"
                >
                    <td className="px-8 py-6 whitespace-nowrap">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center border border-slate-200 shadow-sm overflow-hidden group-hover:scale-105 transition-transform">
                            {contact.avatarUrl ? <img src={contact.avatarUrl} alt="" className="w-full h-full object-cover" /> : <Building2 className="text-slate-400" />}
                        </div>
                        <div>
                        <div className="text-sm font-black text-slate-900 uppercase tracking-tight">{contact.companyName}</div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase mt-1">{contact.country} Node</div>
                        </div>
                    </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                    <div className="space-y-1">
                        <div className="text-[10px] font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 w-fit">TIN: {contact.tin || 'N/A'}</div>
                        {contact.vrn && <div className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 w-fit">VRN: {contact.vrn}</div>}
                    </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                        <div className="flex flex-col">
                            <span className="text-sm font-bold text-slate-700">{contact.contactPerson}</span>
                            <span className="text-[10px] text-slate-400 font-bold uppercase">{contact.role}</span>
                        </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm
                        ${contact.sentiment === 'Positive' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 
                        contact.sentiment === 'Negative' ? 'bg-rose-50 text-rose-700 border-rose-100' : 
                        'bg-slate-50 text-slate-600 border-slate-100'}`}>
                        {contact.sentiment}
                    </span>
                    </td>
                    <td className="px-8 py-6 text-right">
                        <div className="w-8 h-8 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300 group-hover:bg-indigo-600 group-hover:text-white transition-all group-hover:translate-x-1">
                            <ChevronRight size={16} />
                        </div>
                    </td>
                </tr>
                ))}
                {contacts.length === 0 && (
                <tr>
                    <td colSpan={5} className="px-8 py-24 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-[40px] flex items-center justify-center mx-auto mb-6 text-slate-200">
                            <Building2 size={40} />
                        </div>
                        <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Registry Empty</p>
                        {canRegister && (
                          <button onClick={onAddContact} className="mt-4 text-indigo-600 font-black text-xs uppercase hover:underline">Add First Customer</button>
                        )}
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default ContactList;